/**
 * ControlPanel の「機能 (権限) ↔ 画面」の対応表と、出し分けの純粋関数 (権限管理 SBI-6 / SBI-8)。
 *
 * 管理系の 3 画面 (ユーザー管理 / 権限グループ管理 / 権限付与) も ControlPanel 内の画面としてここに含め、
 * ナビの最右に並べる (管理系の機能を持つユーザーにだけ表示される)。
 *
 * 【重要】ここで画面を隠すのは **利便性のため** (押しても使えない画面を見せない) であり、
 * セキュリティ境界ではない。API の認可は常にバックエンドが 403 で拒否する
 * (`lambda/control-panel-api/index.py` の認可 / SBI-5)。URL 直叩きや DevTools での改変で
 * 画面が見えても、API は権限が無ければ 403 を返す。
 *
 * 正本は `lambda/common/permission_features.py` (機能キー・表示名・画面パス)。
 * 本表はフロントの表示に使う写しで、`docs/shared-constants.json` の
 * `permission_features` と一致することを `screens.test.ts` が検証する
 * (画面を足して権限の対象漏れ / 値のずれが起きるのを防ぐ)。
 */

import { MASTER_HISTORY_FEATURE_KEYS } from './masterHistoryFeatures';

export interface ControlPanelScreen {
  /** 機能キー (DynamoDB のグループの `features` に保存される値。改名しない) */
  featureKey: string;
  /** ナビに表示する名前 */
  label: string;
  /**
   * ナビで 2 段に分けて表示するときの行ごとの文字列 (連結すると `label` と一致する)。
   * 長い名前を 1 行で出すとタブが横に伸びるため、一部のタブだけ改行して見せる。
   */
  labelLines?: readonly string[];
  /** ナビのリンク先 (`screenPaths` の先頭) */
  path: string;
  /** この機能に属する画面の URL パス (ナビを持たない従属ルートを含む) */
  screenPaths: readonly string[];
  /** ナビリンクの `data-testid` */
  navTestId: string;
  /**
   * 機能キーに加えて、いずれかを持つことを要求する機能キー (省略時は機能キーだけで表示する)。
   * 「データ変更履歴」は閲覧権限を各マスタ画面の機能に委ねるため、タブ用の機能キーだけを持つ人には
   * 開ける履歴が無い。そのまま表示すると `landingPath` がその画面を指し、ルートガードが
   * 同じ画面へリダイレクトし続けるため、ここで表示自体を止める。
   */
  alsoRequiresAnyOf?: readonly string[];
}

/**
 * ヘッダーナビの並び順 (`docs/shared-constants.json` の機能の順と同じ: ControlPanel → 管理系)。
 * `/ssml-readme` はナビを持たないため `ssml_tuning` に従属させる。
 */
export const CONTROL_PANEL_SCREENS: readonly ControlPanelScreen[] = [
  {
    featureKey: 'broadcast_points',
    label: '放送地点',
    path: '/',
    screenPaths: ['/', '/language-health'],
    navTestId: 'nav-home',
  },
  {
    featureKey: 'devices',
    label: '端末管理',
    path: '/devices',
    screenPaths: ['/devices'],
    navTestId: 'nav-devices',
  },
  {
    featureKey: 'alert_recipients',
    label: '通知先メール管理',
    path: '/alert-recipients',
    screenPaths: ['/alert-recipients'],
    navTestId: 'nav-alert-recipients',
  },
  {
    featureKey: 'chimes',
    label: 'チャイム音設定',
    path: '/chimes',
    screenPaths: ['/chimes'],
    navTestId: 'nav-chimes',
  },
  {
    featureKey: 'phrase_templates',
    label: '定型文設定',
    path: '/phrase-templates',
    screenPaths: ['/phrase-templates'],
    navTestId: 'nav-phrase-templates',
  },
  {
    featureKey: 'ssml_dictionary',
    label: 'SSML辞書',
    path: '/ssml-dictionary',
    screenPaths: ['/ssml-dictionary'],
    navTestId: 'nav-ssml-dictionary',
  },
  {
    featureKey: 'free_text_templates',
    label: '任意テキストテンプレート',
    labelLines: ['任意テキスト', 'テンプレート'],
    path: '/free-text-templates',
    // 共有テキストを統合した。旧 `/synced-texts` は App.tsx でこの画面へリダイレクトする。
    // (リダイレクト元は画面ではないので、バックエンドのカタログ `screen_paths` と同じ 1 件のままにする)
    screenPaths: ['/free-text-templates'],
    navTestId: 'nav-free-text-templates',
  },
  {
    featureKey: 'ssml_tuning',
    label: '音声チューニング',
    path: '/ssml-tuning',
    screenPaths: ['/ssml-tuning', '/ssml-readme'],
    navTestId: 'nav-ssml-tuning',
  },
  {
    featureKey: 'master_history',
    label: 'データ変更履歴',
    path: '/master-history',
    screenPaths: ['/master-history'],
    navTestId: 'nav-master-history',
    alsoRequiresAnyOf: MASTER_HISTORY_FEATURE_KEYS,
  },
  // ---- 管理系 (権限管理 SBI-8) ----
  {
    featureKey: 'user_admin',
    label: 'ユーザー管理',
    path: '/admin/users',
    screenPaths: ['/admin/users'],
    navTestId: 'nav-admin-users',
  },
  {
    featureKey: 'permission_group_admin',
    label: '権限グループ管理',
    path: '/admin/permission-groups',
    screenPaths: ['/admin/permission-groups'],
    navTestId: 'nav-admin-permission-groups',
  },
  {
    featureKey: 'permission_assign',
    label: '権限付与',
    path: '/admin/permission-assignments',
    screenPaths: ['/admin/permission-assignments'],
    navTestId: 'nav-admin-permission-assignments',
  },
];

/** 利用可能な機能キーの集合から、表示してよい画面を (ナビ順に) 返す。 */
export function visibleScreens(features: ReadonlySet<string>): ControlPanelScreen[] {
  return CONTROL_PANEL_SCREENS.filter(
    (screen) =>
      features.has(screen.featureKey) &&
      (screen.alsoRequiresAnyOf === undefined ||
        screen.alsoRequiresAnyOf.some((key) => features.has(key))),
  );
}

/**
 * 許可された先頭の画面のパスを返す。1 つも無ければ `null`。
 *
 * 許可されていない URL への直接アクセスや `/login` への遷移で、行き先に使う。
 */
export function landingPath(features: ReadonlySet<string>): string | null {
  return visibleScreens(features)[0]?.path ?? null;
}
